/**
 * Shared Recharts tooltip. Replaces the near-identical CustomTooltip/PctTooltip/
 * NoteTooltip that each chart defined *inside its component body* — which created
 * a new component identity every render and remounted the tooltip subtree. This
 * one lives at module scope, so it never remounts.
 *
 * Recharts clones the element passed to <Tooltip content={...} /> and injects
 * active/payload/label.
 */
import type { DataNote } from '../../../types/financial';
import { DataNoteList } from '../DataNoteBadge';

export interface ChartTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
  /** Format one row's value. Return null to omit the row entirely. */
  format: (value: number, name: string, entry: any) => string | null;
  /** Pull DataNote[] off payload[0].payload[notesKey] and render them below the rows. */
  notesKey?: string;
  minWidth?: number;
  /** Cap width so a long DataNote wraps instead of stretching the card. */
  maxWidth?: number;
}

export default function ChartTooltip({
  active,
  payload,
  label,
  format,
  notesKey,
  minWidth = 180,
  maxWidth = 320,
}: ChartTooltipProps) {
  if (!active || !payload?.length) return null;

  const rows = payload
    .map((p) => {
      const formatted = p.value == null ? null : format(p.value as number, p.name, p);
      return formatted == null ? null : { name: p.name as string, color: p.color as string, text: formatted };
    })
    .filter((r): r is { name: string; color: string; text: string } => r !== null);

  const notes: DataNote[] | undefined =
    notesKey && payload[0]?.payload ? (payload[0].payload[notesKey] as DataNote[] | undefined) : undefined;

  if (!rows.length && !(notes && notes.length)) return null;

  return (
    <div
      className="bg-white border border-neutral-200 shadow-lg rounded-lg p-3 text-xs space-y-1"
      style={{ minWidth, maxWidth }}
    >
      {label && <p className="font-semibold text-neutral-700 mb-1.5">{label}</p>}
      {rows.map((r) => (
        <div key={r.name} className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm shrink-0 inline-block" style={{ background: r.color }} />
          <span className="text-neutral-600">{r.name}:</span>
          <span className="font-medium ml-auto tabular-nums">{r.text}</span>
        </div>
      ))}
      {notes && notes.length > 0 && (
        <div className="mt-2 pt-2 border-t border-neutral-200 text-neutral-600">
          <DataNoteList notes={notes} />
        </div>
      )}
    </div>
  );
}
